/**
 * Solandia zod schemas.
 *
 * Runtime validation shared by the web app (form validation) and the API
 * (request body validation). Entity schemas describe the *stored* shape;
 * the `*Input` schemas describe accepted create/update payloads.
 *
 * The entity schemas are kept structurally aligned with ./types.ts. Where a
 * `z.infer` would diverge from the hand-written interface we prefer the
 * hand-written interface as the public contract and use these for validation.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export const idSchema = z.string().uuid();

export const isoDateTimeSchema = z
  .string()
  .datetime({ offset: true })
  .describe('ISO-8601 UTC timestamp');

export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .describe('Date-only string');

export const currencyCodeSchema = z
  .string()
  .regex(/^[A-Z]{3}$/, 'Expected ISO-4217 currency code')
  .describe('ISO-4217 currency');

export const minorUnitsSchema = z.number().int();

export const emailSchema = z.string().email().max(320);

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const authProviderSchema = z.enum(['google', 'microsoft', 'email']);

export const familyModeSchema = z.enum(['family', 'separated']);

export const memberRoleSchema = z.enum(['parent', 'trusted']);

export const membershipStatusSchema = z.enum(['invited', 'active']);

export const scopeSchema = z.enum([
  'calendar',
  'contacts',
  'health',
  'documents',
  'messages',
  'expenses',
  'children',
  'shopping',
]);

export const contactTypeSchema = z.enum([
  'doctor',
  'school',
  'dentist',
  'activity',
  'other',
]);

export const calendarEventTypeSchema = z.enum([
  'appointment',
  'handover',
  'custody',
  'school',
  'activity',
  'other',
]);

export const custodyPatternSchema = z.enum(['week-week', '2-2-3', 'custom']);

export const agreementStatusSchema = z.enum([
  'proposed',
  'accepted',
  'declined',
  'withdrawn',
]);

// ---------------------------------------------------------------------------
// Entity schemas
// ---------------------------------------------------------------------------

export const userSchema = z.object({
  id: idSchema,
  email: emailSchema,
  displayName: z.string().min(1).max(120),
  authProvider: authProviderSchema,
  createdAt: isoDateTimeSchema,
});

/**
 * Stored shape of a family.
 *
 * CRITICAL backward-compat: pre-pivot API responses carry no `mode`/
 * `modeChangedAt` fields. `mode` defaults to `'family'` and `modeChangedAt`
 * defaults to `null`, so parsing a legacy `{ id, name, createdAt }` payload
 * still yields a fully-populated, valid Family — nothing breaks on deploy.
 */
export const familySchema = z.object({
  id: idSchema,
  name: z.string().min(1).max(120),
  mode: familyModeSchema.default('family'),
  modeChangedAt: isoDateTimeSchema.nullable().default(null),
  createdAt: isoDateTimeSchema,
});

export const membershipSchema = z.object({
  id: idSchema,
  familyId: idSchema,
  userId: idSchema,
  role: memberRoleSchema,
  permissions: z.array(scopeSchema),
  invitedById: idSchema.nullable(),
  status: membershipStatusSchema,
  createdAt: isoDateTimeSchema,
});

export const childSchema = z.object({
  id: idSchema,
  familyId: idSchema,
  name: z.string().min(1).max(120),
  avatarEmoji: z.string().min(1).max(8),
  birthDate: isoDateSchema,
});

export const contactSchema = z.object({
  id: idSchema,
  childId: idSchema,
  type: contactTypeSchema,
  label: z.string().min(1).max(160),
  phone: z.string().max(40).nullable(),
  address: z.string().max(400).nullable(),
  note: z.string().max(2000).nullable(),
  createdById: idSchema,
  createdAt: isoDateTimeSchema,
});

export const healthRecordSchema = z.object({
  id: idSchema,
  childId: idSchema,
  allergies: z.string().max(2000).nullable(),
  medications: z.string().max(2000).nullable(),
  insurance: z.string().max(400).nullable(),
  bloodType: z.string().max(8).nullable(),
  conditions: z.string().max(2000).nullable(),
  notes: z.string().max(4000).nullable(),
  updatedById: idSchema,
  updatedAt: isoDateTimeSchema,
});

export const calendarEventSchema = z
  .object({
    id: idSchema,
    childId: idSchema,
    title: z.string().min(1).max(200),
    type: calendarEventTypeSchema,
    startAt: isoDateTimeSchema,
    endAt: isoDateTimeSchema,
    allDay: z.boolean(),
    location: z.string().max(400).nullable(),
    note: z.string().max(2000).nullable(),
    createdById: idSchema,
    createdAt: isoDateTimeSchema,
  })
  .refine((e) => e.endAt >= e.startAt, {
    message: 'endAt must be at or after startAt',
    path: ['endAt'],
  });

export const custodyScheduleSchema = z.object({
  id: idSchema,
  childId: idSchema,
  pattern: custodyPatternSchema,
  startDate: isoDateSchema,
  notes: z.string().max(2000).nullable(),
});

export const messageAttachmentSchema = z.object({
  name: z.string().min(1).max(260),
  blobPath: z.string().min(1).max(1024),
  mimeType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
});

export const documentSchema = z.object({
  id: idSchema,
  childId: idSchema,
  name: z.string().min(1).max(260),
  category: z.string().min(1).max(80),
  blobPath: z.string().min(1).max(1024),
  mimeType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
  uploadedById: idSchema,
  note: z.string().max(2000).nullable(),
  createdAt: isoDateTimeSchema,
});

export const messageSchema = z.object({
  id: idSchema,
  familyId: idSchema,
  authorId: idSchema,
  body: z.string().min(1).max(8000),
  attachments: z.array(messageAttachmentSchema),
  createdAt: isoDateTimeSchema,
});

export const expenseSchema = z.object({
  id: idSchema,
  familyId: idSchema,
  childId: idSchema.nullable(),
  payerId: idSchema,
  amountMinor: minorUnitsSchema.nonnegative(),
  currency: currencyCodeSchema,
  category: z.string().min(1).max(80),
  description: z.string().max(2000).nullable(),
  incurredOn: isoDateSchema,
  splitPct: z.number().int().min(0).max(100),
  settledAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

export const shoppingListSchema = z.object({
  id: idSchema,
  familyId: idSchema,
  name: z.string().min(1).max(120),
  createdAt: isoDateTimeSchema,
});

export const shoppingItemSchema = z.object({
  id: idSchema,
  listId: idSchema,
  name: z.string().min(1).max(200),
  category: z.string().max(80),
  assigneeId: idSchema.nullable(),
  checked: z.boolean(),
  checkedAt: isoDateTimeSchema.nullable(),
  createdById: idSchema,
  createdAt: isoDateTimeSchema,
});

export const agreementSchema = z.object({
  id: idSchema,
  familyId: idSchema,
  proposedById: idSchema,
  title: z.string().min(1).max(160),
  body: z.string().min(1).max(8000),
  status: agreementStatusSchema,
  respondedById: idSchema.nullable(),
  respondedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

export const auditEntrySchema = z.object({
  id: idSchema,
  familyId: idSchema,
  actorId: idSchema,
  action: z.string().min(1).max(60),
  entityType: z.string().min(1).max(60),
  entityId: idSchema.nullable(),
  summary: z.string().min(1).max(1000),
  createdAt: isoDateTimeSchema,
});

// ---------------------------------------------------------------------------
// Input schemas (create / update payloads)
// ---------------------------------------------------------------------------

export const updateProfileInputSchema = z.object({
  displayName: z.string().trim().min(1).max(120),
});

export const createFamilyInputSchema = z.object({
  name: z.string().min(1).max(120),
});

export const updateFamilyInputSchema = createFamilyInputSchema.partial();

export const inviteMemberInputSchema = z.object({
  email: emailSchema,
  role: memberRoleSchema,
  permissions: z.array(scopeSchema).optional(),
});

export const updateMemberInputSchema = z.object({
  role: memberRoleSchema.optional(),
  permissions: z.array(scopeSchema).optional(),
});

export const createChildInputSchema = z.object({
  name: z.string().min(1).max(120),
  avatarEmoji: z.string().min(1).max(8),
  birthDate: isoDateSchema,
});

export const updateChildInputSchema = createChildInputSchema.partial();

export const createContactInputSchema = z.object({
  type: contactTypeSchema,
  label: z.string().min(1).max(160),
  phone: z.string().max(40).nullable(),
  address: z.string().max(400).nullable(),
  note: z.string().max(2000).nullable(),
});

export const updateContactInputSchema = createContactInputSchema.partial();

export const updateHealthRecordInputSchema = z
  .object({
    allergies: z.string().max(2000).nullable(),
    medications: z.string().max(2000).nullable(),
    insurance: z.string().max(400).nullable(),
    bloodType: z.string().max(8).nullable(),
    conditions: z.string().max(2000).nullable(),
    notes: z.string().max(4000).nullable(),
  })
  .partial();

export const createCalendarEventInputSchema = z
  .object({
    title: z.string().min(1).max(200),
    type: calendarEventTypeSchema,
    startAt: isoDateTimeSchema,
    endAt: isoDateTimeSchema,
    allDay: z.boolean(),
    location: z.string().max(400).nullable(),
    note: z.string().max(2000).nullable(),
  })
  .refine((e) => e.endAt >= e.startAt, {
    message: 'endAt must be at or after startAt',
    path: ['endAt'],
  });

export const updateCalendarEventInputSchema = z
  .object({
    title: z.string().min(1).max(200),
    type: calendarEventTypeSchema,
    startAt: isoDateTimeSchema,
    endAt: isoDateTimeSchema,
    allDay: z.boolean(),
    location: z.string().max(400).nullable(),
    note: z.string().max(2000).nullable(),
  })
  .partial();

export const upsertCustodyScheduleInputSchema = z.object({
  pattern: custodyPatternSchema,
  startDate: isoDateSchema,
  notes: z.string().max(2000).nullable(),
});

export const createDocumentInputSchema = z.object({
  name: z.string().min(1).max(260),
  category: z.string().min(1).max(80),
  mimeType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
  note: z.string().max(2000).nullable().optional(),
});

export const appendMessageInputSchema = z.object({
  body: z.string().min(1).max(8000),
  attachments: z.array(messageAttachmentSchema).optional(),
});

export const createExpenseInputSchema = z.object({
  childId: idSchema.nullable(),
  amountMinor: minorUnitsSchema.nonnegative(),
  currency: currencyCodeSchema,
  category: z.string().min(1).max(80),
  description: z.string().max(2000).nullable(),
  incurredOn: isoDateSchema,
  splitPct: z.number().int().min(0).max(100).optional(),
});

export const updateExpenseInputSchema = createExpenseInputSchema.partial();

export const proposeAgreementInputSchema = z.object({
  title: z.string().min(1).max(160),
  body: z.string().min(1).max(8000),
});

export const createShoppingListInputSchema = z.object({
  name: z.string().min(1).max(120),
});

export const addShoppingItemInputSchema = z.object({
  name: z.string().min(1).max(200),
  category: z.string().max(80).optional(),
  assigneeId: idSchema.nullable().optional(),
});

export const updateShoppingItemInputSchema = z
  .object({
    name: z.string().min(1).max(200),
    category: z.string().max(80),
    assigneeId: idSchema.nullable(),
    checked: z.boolean(),
  })
  .partial();

// ---------------------------------------------------------------------------
// Registry — handy for generic dispatchers / tests.
// ---------------------------------------------------------------------------

export const entitySchemas = {
  user: userSchema,
  family: familySchema,
  membership: membershipSchema,
  child: childSchema,
  contact: contactSchema,
  healthRecord: healthRecordSchema,
  calendarEvent: calendarEventSchema,
  custodySchedule: custodyScheduleSchema,
  document: documentSchema,
  message: messageSchema,
  expense: expenseSchema,
  shoppingList: shoppingListSchema,
  shoppingItem: shoppingItemSchema,
  agreement: agreementSchema,
  auditEntry: auditEntrySchema,
} as const;
